import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { StatusModal } from 'src/components/StatusModal'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { useCreateAiListeningPromptMutation } from 'src/reactQuery/useCreateAiListeningPromptMutation'
import {
  MIN_PROMPT_CONTEXT_LENGTH,
  useGetAiListeningPromptContextQuery,
} from 'src/reactQuery/useGetAiListeningPromptContextQuery'
import {
  AiTool,
  CreatePromptPayload,
} from 'src/services/aiListeningApi/brandProfile.types'
import styles from './addPrompt.module.scss'
import { ContextTags, SparkleIcon } from '../ContextTags'
import { useDebouncedValue } from '../hooks'

const DETECT_DEBOUNCE_MS = 600
const MAX_PROMPT_LENGTH = 5000

interface AddPromptFlowProps {
  isOpen: boolean
  onClose: () => void
  // "View Brand Profile" on the success modal; omitted = just close.
  onViewProfile?: () => void
  profile: { _id: string; aiTools: AiTool[] }
}

type Step = 'error' | 'form' | 'success'

// Add Prompt: the form modal, then "Prompt added" (add another / view the
// profile) or "Failed to add" (cancel / try again with the same text).
export const AddPromptFlow: FC<AddPromptFlowProps> = ({
  isOpen,
  onClose,
  onViewProfile,
  profile,
}) => {
  const [step, setStep] = useState<Step>('form')
  const [text, setText] = useState('')
  const [lastPayload, setLastPayload] = useState<CreatePromptPayload | null>(
    null
  )

  const debouncedText = useDebouncedValue(text, DETECT_DEBOUNCE_MS)
  const trimmed = text.trim()
  const isLongEnough = trimmed.length >= MIN_PROMPT_CONTEXT_LENGTH
  const {
    data: context,
    isError: isContextError,
    isFetching: isDetecting,
  } = useGetAiListeningPromptContextQuery(isOpen ? debouncedText : '')
  // Tags belong to the text on screen only once the debounce has caught up.
  const isSettled = debouncedText === text
  const tags = isSettled && isLongEnough ? context?.tags ?? [] : []
  const isContextPending = isLongEnough && (!isSettled || isDetecting)

  const createMutation = useCreateAiListeningPromptMutation({
    onError: () => setStep('error'),
    onSuccess: () => setStep('success'),
  })

  useEffect(() => {
    if (isOpen) {
      setStep('form')
      setText('')
      setLastPayload(null)
    }
  }, [isOpen])

  useDisableScroll(isOpen && step === 'form')

  const submit = (payload: CreatePromptPayload) => {
    setLastPayload(payload)
    createMutation.mutate({ payload, profileId: profile._id })
  }

  const handleSubmit = () => {
    if (!trimmed || createMutation.isPending) return
    submit({
      contextTags: tags.length ? tags : undefined,
      prompt: trimmed,
    })
  }

  const startOver = () => {
    setText('')
    setLastPayload(null)
    setStep('form')
  }

  if (!isOpen) return null

  const modelsLabel = profile.aiTools.length
    ? profile.aiTools.join(', ')
    : 'your AI models'

  if (step === 'success') {
    return (
      <StatusModal
        doneLabel={onViewProfile ? 'View Brand Profile' : 'Done'}
        hideCountdown
        isOpen
        message={
          <>
            <strong className={styles.quoted}>“{lastPayload?.prompt}”</strong>{' '}
            is now being tracked on {modelsLabel}. First results in a few
            minutes.
          </>
        }
        onClose={onClose}
        onDiscard={startOver}
        onSecondaryAction={onViewProfile ?? onClose}
        secondaryLabel="Add another Prompt"
        title="Prompt added Successfully!"
        type="success"
      />
    )
  }

  if (step === 'error') {
    return (
      <StatusModal
        doneLabel={createMutation.isPending ? 'Trying…' : 'Try again'}
        hideCountdown
        isOpen
        message={
          <>
            Something went wrong. We couldn&apos;t add the prompt to your brand
            profile.
            <br />
            Please try again.
          </>
        }
        onClose={onClose}
        onSecondaryAction={() => lastPayload && submit(lastPayload)}
        secondaryLabel="Cancel"
        title="Failed to Add Prompt"
        type="error"
      />
    )
  }

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        aria-labelledby="add-prompt-title"
        aria-modal="true"
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className={styles.header}>
          <div>
            <h2 className={styles.title} id="add-prompt-title">
              Add Prompt
            </h2>
            <p className={styles.subtitle}>
              A question your customers might ask an AI model.
            </p>
          </div>
          <button
            aria-label="Close"
            className={styles.close}
            onClick={onClose}
            type="button"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        <label className={styles.label} htmlFor="add-prompt-text">
          {trimmed ? 'Prompt' : 'Enter prompt'}
        </label>
        <textarea
          autoFocus
          className={styles.textarea}
          id="add-prompt-text"
          maxLength={MAX_PROMPT_LENGTH}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              handleSubmit()
            }
          }}
          placeholder="Example: Which SIM card should I buy in Georgia?"
          value={text}
        />

        <div className={styles.context}>
          {isLongEnough ? (
            <>
              <span className={styles.contextLabel}>
                <SparkleIcon />
                Detected Context
                {isContextPending && (
                  <span aria-label="Detecting context" className={styles.dots}>
                    <span />
                    <span />
                    <span />
                  </span>
                )}
              </span>
              <span className={styles.contextTags}>
                {isContextPending && (
                  <>
                    <span className={styles.tagPlaceholder} />
                    <span className={styles.tagPlaceholder} />
                    <span className={styles.tagPlaceholder} />
                  </>
                )}
                {!isContextPending && tags.length > 0 && (
                  <ContextTags tags={tags} />
                )}
                {!isContextPending && tags.length === 0 && (
                  <span className={styles.contextEmpty}>
                    {isContextError
                      ? 'Context could not be detected — the prompt can still be added'
                      : 'No context detected'}
                  </span>
                )}
              </span>
            </>
          ) : (
            <span className={styles.contextHint}>
              <SparkleIcon />
              Context tags will appear automatically once a prompt is entered.
            </span>
          )}
        </div>

        <div className={styles.actions}>
          <button className={styles.cancel} onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className={styles.submit}
            disabled={!trimmed || createMutation.isPending}
            onClick={handleSubmit}
            type="button"
          >
            {createMutation.isPending ? 'Adding…' : 'Add Prompt'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
